// Lays out the bottom-center chrome group: the control cluster (#bottom-cluster)
// and the colorbar (#colorbar-container) are centered together as one unit. The
// cluster's width is variable (the coord readout is auto-width) and the colorbar
// collapses, so the geometry is measured at runtime rather than hardcoded.
//
// The lower-left corner gimbal (#camera-gnomon) is a separate fixed element. When
// it is on, the centered group slides right to clear it; when the group then runs
// out of horizontal room, the colorbar reflows to a vertical bar pinned
// bottom-right and the cluster centers alone in the remaining band.
//
// A real user drag detaches the colorbar (data-docked → "false") and hands
// control to magneticSnap + resolveCollapsedOverlap; dropping it back *close to
// the cluster* (small horizontal gap, within the bottom band) re-docks it into
// the group. Dropped far along the bottom it stays floating where placed and the
// cluster re-centers alone. No persistence — reload returns to the HTML default
// (data-docked="true") and recomputes.

import { resolveCollapsedOverlap } from '../draggablePanels';

const GROUP_GAP_PX = 10; // gap between the cluster's right edge and the bar
const GIMBAL_GAP_PX = 12; // gap kept clear of the corner gimbal's right edge
const VIEWPORT_MARGIN_PX = 8; // edge clearance for the horizontal fit test
const EDGE_GAP_PX = 15; // inset when docked vertically at the right edge
const BOTTOM_PX = 8; // shared bottom inset with the control cluster
const DOCK_PROXIMITY_PX = 72; // horizontal gap to the cluster below which a dropped colorbar re-docks
const DOCK_BAND_PX = 160; // bottom band (from viewport bottom) a drop must land in to be eligible

export function installColorbarDock(): () => void {
    const colorbar = document.getElementById('colorbar-container');
    if (!colorbar) return () => { /* colorbar markup missing */ };
    const cluster = document.getElementById('bottom-cluster');

    // Set the edge attribute and repaint the gradient orientation via the same
    // CustomEvent magneticSnap fires (colorbar.ts listens and repaints).
    const setEdge = (edge: 'bottom' | 'right'): void => {
        if (colorbar.dataset.edge === edge) return;
        colorbar.dataset.edge = edge;
        colorbar.dispatchEvent(new CustomEvent('paneedgechange', { detail: { edge } }));
    };

    const setClusterLeft = (px: number): void => {
        if (!cluster) return;
        cluster.style.left = `${Math.round(px)}px`;
        cluster.style.transform = 'none'; // drop the CSS centering fallback
    };

    // Right edge of whatever occupies the lower-left corner in viewport px: the
    // gnomon, or the perf overlay when stats are shown (it replaces the gnomon
    // there). 0 when neither is visible (display:none → zero-width rect). The
    // centered group clears this so it never sits under the corner widget.
    const cornerRight = (): number => {
        let edge = 0;
        for (const id of ['camera-gnomon', 'perf-container']) {
            const el = document.getElementById(id);
            if (!el) continue;
            const r = el.getBoundingClientRect();
            if (r.width > 0) edge = Math.max(edge, r.right);
        }
        return edge;
    };

    // Docked: center [cluster + colorbar], clearing the gimbal; reflow vertical
    // when it no longer fits horizontally.
    const relayoutDocked = (): void => {
        const clusterW = cluster ? cluster.offsetWidth : 0;
        const gRight = cornerRight();
        const minLeft = gRight > 0 ? gRight + GIMBAL_GAP_PX : VIEWPORT_MARGIN_PX;
        const maxRight = window.innerWidth - VIEWPORT_MARGIN_PX;

        // Measure the colorbar in its horizontal orientation, then decide.
        setEdge('bottom');
        const colorbarW = colorbar.offsetWidth;
        const groupW = clusterW + GROUP_GAP_PX + colorbarW;

        let groupLeft = (window.innerWidth - groupW) / 2;
        if (groupLeft < minLeft) groupLeft = minLeft;

        if (groupLeft + groupW <= maxRight) {
            // Horizontal: cluster then colorbar, centered (and gimbal-cleared).
            setClusterLeft(groupLeft);
            colorbar.style.left = `${Math.round(groupLeft + clusterW + GROUP_GAP_PX)}px`;
            colorbar.style.right = 'auto';
        } else {
            // Vertical: colorbar pinned bottom-right; cluster centers alone in
            // the band between the gimbal and the vertical bar.
            setEdge('right');
            colorbar.style.right = `${EDGE_GAP_PX}px`;
            colorbar.style.left = 'auto';
            const verticalW = colorbar.offsetWidth; // re-measured after the flip
            const bandLeft = minLeft;
            const bandRight = window.innerWidth - EDGE_GAP_PX - verticalW - GIMBAL_GAP_PX;
            let clusterLeft = (window.innerWidth - clusterW) / 2;
            if (clusterLeft < bandLeft) clusterLeft = bandLeft;
            if (clusterLeft + clusterW > bandRight) clusterLeft = Math.max(bandLeft, bandRight - clusterW);
            setClusterLeft(clusterLeft);
        }

        colorbar.style.bottom = `${BOTTOM_PX}px`;
        colorbar.style.top = 'auto';

        // Clear any residual overlap (e.g. a secondary colorbar at bottom-right
        // when docked vertically). No-op for the horizontal dock.
        resolveCollapsedOverlap(colorbar);
    };

    // Floating colorbar (user dragged it off): center the cluster alone, leave
    // the bar where it was dropped, but spring it clear if chrome grew into it.
    const relayoutFloating = (): void => {
        const clusterW = cluster ? cluster.offsetWidth : 0;
        const gRight = cornerRight();
        const minLeft = gRight > 0 ? gRight + GIMBAL_GAP_PX : VIEWPORT_MARGIN_PX;
        let clusterLeft = (window.innerWidth - clusterW) / 2;
        if (clusterLeft < minLeft) clusterLeft = minLeft;
        setClusterLeft(clusterLeft);
        resolveCollapsedOverlap(colorbar);
    };

    const onLayoutChange = (): void => {
        if (colorbar.classList.contains('dragging')) return; // makeDraggable owns it mid-drag
        if (colorbar.dataset.docked === 'true') relayoutDocked();
        else relayoutFloating();
    };

    // rAF-debounced so a burst of resize / observer callbacks repositions once.
    let rafId = 0;
    const schedule = (): void => {
        if (rafId) return;
        rafId = requestAnimationFrame(() => { rafId = 0; onLayoutChange(); });
    };

    window.addEventListener('resize', schedule);

    // Re-center when either box changes size. The cluster moves its right edge
    // when the coord text or projection/nav chips re-skin. The colorbar's
    // collapse/expand animates its size over 300ms (the .pane-content grid-rows
    // transition), so observe it too: the ResizeObserver fires throughout the
    // animation and at its end. The class MutationObserver alone sampled only the
    // start (stale pre-collapse size), leaving the cluster off-center after a
    // collapse. Position-only writes from relayout don't resize, so no loop.
    const ro = new ResizeObserver(schedule);
    if (cluster) ro.observe(cluster);
    ro.observe(colorbar);

    // Corner widgets toggle body classes (`gnomon-collapsed`, `stats-shown`).
    // Watching the body class re-lays-out the group when the gnomon or perf
    // overlay appears/disappears — robust to install order (elements may not
    // exist yet) and to stats replacing the gnomon in the corner.
    const bodyMo = new MutationObserver(schedule);
    bodyMo.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    // Detect a real drag-commit and collapse/expand via the colorbar's class.
    // `.dragging` is added only on a threshold-exceeding drag and removed in
    // endDrag *before* magneticSnap + resolveCollapsedOverlap run synchronously,
    // so by the time this observer's microtask fires the bar is already snapped
    // and chrome-cleared — we only decide re-dock vs. stay-floating.
    let wasDragging = colorbar.classList.contains('dragging');
    const mo = new MutationObserver(() => {
        const dragging = colorbar.classList.contains('dragging');
        if (dragging && !wasDragging) { wasDragging = true; return; }
        if (!dragging && wasDragging) {
            wasDragging = false;
            const r = colorbar.getBoundingClientRect();
            const cy = r.top + r.height / 2;
            // Re-dock only when dropped close to the buttons: within the bottom
            // band AND a small horizontal gap from the cluster. The gap is the
            // distance between the two boxes' nearest edges (0 when they overlap),
            // so the test reads against where the cluster actually sits right now.
            // A drop far along the bottom stays floating where placed; the cluster
            // then re-centers alone (relayoutFloating).
            const inBand = cy > window.innerHeight - DOCK_BAND_PX;
            const cr = cluster?.getBoundingClientRect();
            const gap = cr
                ? Math.max(cr.left - r.right, r.left - cr.right, 0)
                : Number.POSITIVE_INFINITY;
            colorbar.dataset.docked = inBand && gap < DOCK_PROXIMITY_PX ? 'true' : 'false';
            schedule();
            return;
        }
        // Collapse/expand (class toggled without a drag) changes the bar's
        // size — re-dock if docked, else spring clear if it now hits chrome.
        if (!dragging) schedule();
    });
    mo.observe(colorbar, { attributes: true, attributeFilter: ['class'] });

    schedule();

    return () => {
        if (rafId) cancelAnimationFrame(rafId);
        window.removeEventListener('resize', schedule);
        ro.disconnect();
        bodyMo.disconnect();
        mo.disconnect();
    };
}
